'use client';

import { forwardRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { stegaClean } from 'next-sanity';
import { cn } from '@/lib/utils';
import type { RulesBlock, RulesRule, RulesVariant } from '@/sanity/pages/PageGeneralRulesV3Template';
import { ArrowRightIcon, RuleIcon } from './Icons';
import {
  AppliesChip,
  BreachSequence,
  Callout,
  KeyValueTable,
  ProductCards,
  RuleBox,
  StatCards,
  Steps,
  TextBlock,
  TimeCards,
} from './primitives';
import { Figure } from './figures';

const ease = [0.25, 0.1, 0.25, 1] as const;

function Block({ block }: { block: RulesBlock }) {
  switch (block._type) {
    case 'textBlock':
      return <TextBlock heading={block.heading} body={block.body} bodyWidth={block.bodyWidth} size={block.size} />;
    case 'callout':
      return <Callout tone={block.tone} icon={block.icon} title={block.title} body={block.body} />;
    case 'statCards':
      return <StatCards cards={block.cards} />;
    case 'breachSequence':
      return <BreachSequence steps={block.steps} />;
    case 'timeCards':
      return <TimeCards cards={block.cards} />;
    case 'productCards':
      return <ProductCards cards={block.cards} />;
    case 'steps':
      return (
        <Steps
          title={block.title}
          icon={block.icon}
          steps={block.steps}
          tableLabel={block.tableLabel}
          tableRows={block.tableRows}
          tableNote={block.tableNote}
        />
      );
    case 'keyValueTable':
      return <KeyValueTable label={block.label} rows={block.rows} note={block.note} />;
    case 'figure':
      return <Figure preset={stegaClean(block.preset)} image={block.image} imageMobile={block.imageMobile} />;
    default:
      return null;
  }
}

export interface RuleDetailProps {
  rule: RulesRule;
  variantIndex: number;
  onVariantChange: (index: number) => void;
  nextRule?: { slug: string; title: string };
  onNext: (slug: string) => void;
  appliesToLabel?: string;
  nextRuleLabel?: string;
}

const RuleDetail = forwardRef<HTMLDivElement, RuleDetailProps>(function RuleDetail(
  { rule, variantIndex, onVariantChange, nextRule, onNext, appliesToLabel, nextRuleLabel },
  ref,
) {
  const variants: RulesVariant[] = rule.variants ?? [];
  const variant = variants[variantIndex] ?? variants[0];
  const title = variant?.title || rule.title;
  const hasSelector = variants.length > 1;
  const appliesTo = variant?.appliesTo ?? [];

  return (
    <div ref={ref} data-ui="rules-v3.rule" className="flex min-w-0 w-full flex-col gap-[4rem] scroll-mt-[12rem]">
      {/* ── Header: icon + title (+ variant selector) ─────────────────────── */}
      <div data-ui="rule.header" className="flex w-full flex-col gap-[2rem] lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-[2rem]">
          <div data-ui="rule.icon-box" className="flex h-[6rem] w-[6rem] shrink-0 items-center justify-center rounded-[0.25rem] bg-[#f2f4f3] text-black">
            <RuleIcon data-ui="rule.icon" type={stegaClean(variant?.iconType || rule.iconType)} className="h-[3rem] w-[3rem]" />
          </div>
          <h1 data-ui="rule.title" className="mb-0 font-geist text-[4rem] font-medium leading-[1.1] text-black lg:text-[5rem] lg:leading-[6rem]">
            {title}
          </h1>
        </div>
        {hasSelector && (
          <div
            role="tablist"
            data-ui="rule.tabs"
            className="flex w-full gap-[2rem] rounded-[0.25rem] border border-black/10 p-[0.5rem] lg:w-auto lg:gap-0"
          >
            {variants.map((v, i) => {
              const active = i === variantIndex;
              return (
                <button
                  key={v._key ?? i}
                  type="button"
                  role="tab"
                  data-ui={active ? 'rule.tab.active' : 'rule.tab'}
                  aria-selected={active}
                  onClick={() => onVariantChange(i)}
                  className={cn(
                    'flex h-[3.5rem] flex-1 items-center justify-center whitespace-nowrap rounded-[0.25rem] px-[2rem] font-geist text-[2rem] font-medium leading-[3rem] transition-colors lg:flex-none',
                    active ? 'bg-black text-white' : 'text-black/[0.64] hover:text-black',
                  )}
                >
                  {v.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Applies to ───────────────────────────────────────────────────── */}
      {appliesTo.length > 0 && (
        <div data-ui="rule.applies" className="flex w-full flex-col gap-[1rem] lg:flex-row lg:flex-wrap lg:items-start lg:gap-x-[1rem] lg:gap-y-0">
          <p data-ui="rule.applies-label" className="mb-0 whitespace-nowrap font-geist text-[2rem] font-medium uppercase leading-[3rem] text-black/40">
            {appliesToLabel ?? 'Applies to'}
          </p>
          <div className="flex min-w-0 flex-1 flex-wrap gap-[1rem]">
            {appliesTo.map((chip, i) => (
              <AppliesChip key={i}>{chip}</AppliesChip>
            ))}
          </div>
        </div>
      )}

      <div data-ui="rule.divider" className="h-px w-full bg-black/10" />

      {/* ── Boxes ────────────────────────────────────────────────────────── */}
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={`${rule._key}-${variantIndex}`}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.28, ease }}
          className="flex w-full flex-col gap-[4rem]"
        >
          {(variant?.sections ?? []).map((section) => (
            <RuleBox key={section._key} data-ui="rule.box">
              {(section.blocks ?? []).map((block) => (
                <Block key={block._key} block={block} />
              ))}
            </RuleBox>
          ))}
        </motion.div>
      </AnimatePresence>

      {/* ── Next rule ────────────────────────────────────────────────────── */}
      {nextRule && (
        <button
          type="button"
          data-ui="rule.next"
          onClick={() => onNext(nextRule.slug)}
          className="group flex w-full items-center justify-between gap-[2rem] rounded-[0.25rem] bg-[#f2f4f3]/50 p-[2rem] text-left transition-colors hover:bg-[#f2f4f3] lg:px-[3rem] lg:py-[2.5rem]"
        >
          <span className="flex min-w-0 flex-col gap-[0.5rem] font-geist">
            <span data-ui="rule.next-label" className="text-[2rem] leading-[3rem] text-black/[0.64]">{nextRuleLabel ?? 'Next rule'}</span>
            <span data-ui="rule.next-title" className="text-[2.5rem] font-medium leading-[3.5rem] text-black">{nextRule.title}</span>
          </span>
          <span data-ui="rule.next-arrow" className="flex h-[5rem] w-[5rem] shrink-0 items-center justify-center rounded-[0.25rem] bg-black text-white transition-transform group-hover:translate-x-[0.25rem]">
            <ArrowRightIcon className="h-[2rem] w-[2rem]" />
          </span>
        </button>
      )}
    </div>
  );
});

export default RuleDetail;
